// src/admin/pages/CampaignsAdminPage.tsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Button,
  Stack,
  Alert,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  ADMIN_CAMPAIGNS_QUERY,
  CREATE_CAMPAIGN_MUTATION,
  UPDATE_CAMPAIGN_MUTATION,
  DELETE_CAMPAIGN_MUTATION,
} from "../../graphql/campaigns";
import AdminLoading from "../Components/AdminLoading";
import ConfirmDialog from "../Components/ConfirmDialog";
import CampaignFormDialog, {
  Campaign as CampaignFormValues,
} from "../Components/CampaignFormDialog";

type Campaign = {
  id: string;
  name: string;
  images: string[];
};

const CampaignsAdminPage: React.FC = () => {
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(ADMIN_CAMPAIGNS_QUERY);
  const [createCampaign, { loading: creating }] = useMutation(
    CREATE_CAMPAIGN_MUTATION,
    { refetchQueries: [{ query: ADMIN_CAMPAIGNS_QUERY }] }
  );
  const [updateCampaign, { loading: updating }] = useMutation(
    UPDATE_CAMPAIGN_MUTATION,
    { refetchQueries: [{ query: ADMIN_CAMPAIGNS_QUERY }] }
  );
  const [deleteCampaign, { loading: deleting }] = useMutation(
    DELETE_CAMPAIGN_MUTATION,
    { refetchQueries: [{ query: ADMIN_CAMPAIGNS_QUERY }] }
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Campaign | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  const campaigns: Campaign[] = data?.campaigns || [];

  const filtered = useMemo(
    () =>
      campaigns.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
      ),
    [campaigns, search]
  );

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const busy = loading || creating || updating || deleting;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const startCreate = () => {
    setEditingCampaign(null);
    setDialogOpen(true);
  };

  const handleFormSubmit = async (values: CampaignFormValues) => {
    setActionError(null);

    const vars = {
      id: values.id,
      name: values.name,
      images: values.images,
    };

    try {
      if (values.id) {
        await updateCampaign({ variables: vars });
      } else {
        const { id, ...rest } = vars;
        await createCampaign({ variables: rest });
      }
    } catch (err: any) {
      setActionError(err.message);
      throw err;
    }
  };

  const askDelete = (c: Campaign) => {
    setToDelete(c);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setActionError(null);
    try {
      await deleteCampaign({ variables: { id: toDelete.id } });
      setConfirmOpen(false);
      setToDelete(null);
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  return (
    <>
      <AdminLoading open={busy} />
      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          mb={2}
          spacing={2}
        >
          <Typography variant="h5">Campaigns</Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={startCreate}
            >
              New Campaign
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load campaigns: {error.message}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Image Count</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No campaigns found.
                    </TableCell>
                  </TableRow>
                )}

                {paged.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.images?.length || 0}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/campaigns/${c.id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => askDelete(c)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      <CampaignFormDialog
        open={dialogOpen}
        initialCampaign={
          editingCampaign
            ? {
                id: editingCampaign.id,
                name: editingCampaign.name,
                images: editingCampaign.images || [],
              }
            : null
        }
        onClose={() => setDialogOpen(false)}
        onSubmit={handleFormSubmit}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Delete campaign?"
        description={
          toDelete ? `Are you sure you want to delete "${toDelete.name}"?` : ""
        }
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default CampaignsAdminPage;
